import { Injectable, Injector } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { I18nService } from '../services/i18n.service';
import { LoggerService } from '../services/logger.service';
import { environment } from '../../../environments/environment';


@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private static readonly StationIdHeader = 'X-Autolac-Station-Id';
  private static readonly UsuarioSoHeader = 'X-Autolac-Usuario-So';
  private static readonly RemoteSessionHeader = 'X-Autolac-Remote-Session';
  private static readonly LocalStorageStationKey = 'autolac_station_id';
  private static readonly LocalStorageUsuarioSoKey = 'autolac_usuario_so';
  private static readonly LocalStorageRemoteSessionKey = 'autolac_remote_session';
  private static readonly CultureHeader = 'X-Culture';
  private static readonly SessionIdHeader = 'X-Session-Id';

  constructor(private injector: Injector) {}

  /**
   * Encaminha o ID do posto/PC para a API, para leitura de {@code PARAMETROS_SISTEMA_PC} (local padrão, parâmetro 43).
   * Aplica a todas as rotas da API, inclusive login e refresh (antes do token).
   */
  private withStationContext(request: HttpRequest<unknown>): HttpRequest<unknown> {
    // Feature flag US 59421: desligada, não injeta nenhum header de estação (X-Autolac-*),
    // mesmo que o localStorage ainda tenha valores de sessões antigas. Reverte ao comportamento
    // pré-59421 (backend cai no parâmetro global). Reativar: environment.features.stationContext.
    if (!environment.features?.stationContext) {
      return request;
    }

    const hasLocalStorage = typeof localStorage !== 'undefined';
    const fromStorage = hasLocalStorage
      ? localStorage.getItem(AuthInterceptor.LocalStorageStationKey)?.trim()
      : undefined;
    const fromEnv = environment.stationId?.trim() ?? '';
    const station = (fromStorage || fromEnv) || null;
    if (!station) {
      return request;
    }

    const rs = hasLocalStorage
      ? localStorage.getItem(AuthInterceptor.LocalStorageRemoteSessionKey)?.trim()
      : undefined;
    let sessionStr: string;
    if (rs !== undefined && rs !== '' && !Number.isNaN(Number.parseInt(rs, 10))) {
      sessionStr = String(Number.parseInt(rs, 10));
    } else if (!fromStorage && fromEnv) {
      // Estação veio do environment (cenário local/dev): respeita o override do environment.
      sessionStr = String(environment.remoteSession ?? 1);
    } else {
      // Fluxo AVD/web normal: sessão remota por padrão (alinha ao que o legado grava em AVD).
      sessionStr = '1';
    }

    const headers: { [key: string]: string } = {
      [AuthInterceptor.StationIdHeader]: station,
      [AuthInterceptor.RemoteSessionHeader]: sessionStr
    };

    const usuarioSo = hasLocalStorage
      ? localStorage.getItem(AuthInterceptor.LocalStorageUsuarioSoKey)?.trim()
      : undefined;
    if (usuarioSo) {
      headers[AuthInterceptor.UsuarioSoHeader] = usuarioSo;
    }

    return request.clone({ setHeaders: headers });
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const url = request.url;
    const isApiRequest = url.includes(environment.apiUrl) || url.startsWith('/api/');
    
    const isAuthEndpoint =
      url.includes('/auth/login') ||
      url.includes('/auth/validate-token') ||
      url.includes('/auth/callback') ||
      url.includes('/auth/refresh');
    
    console.log('🔍 AUTH INTERCEPTOR - Interceptando requisição:', {
      url,
      method: request.method,
      isApiRequest,
      isAuthEndpoint
    });

    if (!isApiRequest) {
      console.log('⏭️ AUTH INTERCEPTOR - Requisição não é da API, ignorando');
      return next.handle(request);
    }

    request = this.withStationContext(request);

    if (isAuthEndpoint) {
      console.log('⏭️ AUTH INTERCEPTOR - Requisição de autenticação, não adicionando token');
      return next.handle(request);
    }

    const authService = this.injector.get(AuthService);
    const logger = this.injector.get(LoggerService);

    const idToken = authService.getIdToken();
    const accessToken = authService.getAccessToken();
    const genericToken = authService.getToken();

    console.log('🔑 AUTH INTERCEPTOR - Tokens encontrados via AuthService:', {
      hasIdToken: !!idToken,
      hasAccessToken: !!accessToken,
      hasGenericToken: !!genericToken,
      idTokenLength: idToken?.length,
      accessTokenLength: accessToken?.length,
      genericTokenLength: genericToken?.length,
      priority: idToken ? '✅ ID Token (correto para API)' : accessToken ? '⚠️ Access Token (fallback)' : '⚠️ Generic Token (fallback)'
    });

    let token = idToken || accessToken || genericToken;

    if (!token) {
      console.warn('⚠️ AUTH INTERCEPTOR - Nenhum token via AuthService, verificando sessionStorage diretamente...');
      const directIdToken = sessionStorage.getItem(environment.auth.idTokenStorageKey);
      const directAccessToken = sessionStorage.getItem(environment.auth.accessTokenStorageKey);
      const directGenericToken = sessionStorage.getItem(environment.auth.tokenStorageKey);
      
      token = directIdToken || directAccessToken || directGenericToken;
      
      if (token) {
        const source = directIdToken ? 'idToken (PRIORIDADE - correto para API)' : directAccessToken ? 'accessToken (fallback)' : 'genericToken (fallback)';
        console.log('✅ AUTH INTERCEPTOR - Token encontrado diretamente no sessionStorage!');
        console.log('✅ AUTH INTERCEPTOR - Token source:', source);
        console.log('✅ AUTH INTERCEPTOR - Token length:', token.length);
        console.log('✅ AUTH INTERCEPTOR - Token preview:', token.substring(0, 50) + '...');
        if (!directIdToken) {
          console.warn('⚠️ AUTH INTERCEPTOR - ID Token não encontrado! Usando fallback. Isso pode causar erro 401.');
        }
      } else {
        console.error('❌ AUTH INTERCEPTOR - NENHUM TOKEN encontrado nem no AuthService nem no sessionStorage!');
        console.error('❌ AUTH INTERCEPTOR - Chaves verificadas:', {
          accessTokenKey: environment.auth.accessTokenStorageKey,
          idTokenKey: environment.auth.idTokenStorageKey,
          genericTokenKey: environment.auth.tokenStorageKey
        });
        console.error('❌ AUTH INTERCEPTOR - Valores no sessionStorage:', {
          idToken: directIdToken ? directIdToken.substring(0, 30) + '...' : 'NÃO ENCONTRADO',
          accessToken: directAccessToken ? directAccessToken.substring(0, 30) + '...' : 'NÃO ENCONTRADO',
          genericToken: directGenericToken ? directGenericToken.substring(0, 30) + '...' : 'NÃO ENCONTRADO'
        });
      }
    } else if (!idToken) {
      console.warn('⚠️ AUTH INTERCEPTOR - ID Token não encontrado! Usando fallback. Isso pode causar erro 401.');
      console.warn('⚠️ AUTH INTERCEPTOR - O backend espera ID Token no header Authorization.');
    }

    logger.log('=== AUTH INTERCEPTOR ===');
    logger.log('URL:', url);
    logger.log('Método:', request.method);
    logger.log('Has IdToken:', !!idToken, idToken ? '(✅ PRIORIDADE - correto para API)' : '(❌ NÃO ENCONTRADO)');
    logger.log('Has AccessToken:', !!accessToken, accessToken ? '(⚠️ Fallback - não recomendado para API)' : '');
    logger.log('Has GenericToken:', !!genericToken);
    logger.log('Token final:', token ? token.substring(0, 20) + '...' : 'NENHUM');
    logger.log('Token source:', idToken ? 'ID Token (✅ correto)' : accessToken ? 'Access Token (⚠️ pode causar 401)' : genericToken ? 'Generic Token (⚠️ pode causar 401)' : 'NENHUM');

    if (token && token.trim().length > 0) {
      const language = this.injector.get(I18nService).getCurrentLanguage();

      const storedSessionId = typeof sessionStorage !== 'undefined'
        ? sessionStorage.getItem(environment.auth.sessionIdStorageKey)?.trim() || null
        : null;

      const additionalHeaders: { [key: string]: string } = {
        Authorization: `Bearer ${token}`,
        [AuthInterceptor.CultureHeader]: language
      };
      if (storedSessionId) {
        additionalHeaders[AuthInterceptor.SessionIdHeader] = storedSessionId;
      }

      const clonedRequest = request.clone({ setHeaders: additionalHeaders });

      const authHeader = `Bearer ${token}`;
      const tokenType = idToken ? 'ID Token (✅ correto para API)' : accessToken ? 'Access Token (⚠️ pode causar 401)' : 'Generic Token (⚠️ pode causar 401)';
      console.log('✅ AUTH INTERCEPTOR - Token adicionado ao header');
      console.log('📋 AUTH INTERCEPTOR - Token type:', tokenType);
      console.log('📋 AUTH INTERCEPTOR - Header Authorization:', authHeader.substring(0, 50) + '...');
      console.log('📋 AUTH INTERCEPTOR - Headers completos:', clonedRequest.headers.keys());
      console.log('📋 AUTH INTERCEPTOR - Verificando header Authorization na requisição:', clonedRequest.headers.get('Authorization')?.substring(0, 50) + '...');
      
      if (!idToken) {
        console.warn('⚠️ AUTH INTERCEPTOR - ATENÇÃO: Usando token que NÃO é ID Token!');
        console.warn('⚠️ AUTH INTERCEPTOR - O backend espera ID Token. Isso pode causar erro 401.');
      }

      const allHeaders: { [key: string]: string } = {};
      clonedRequest.headers.keys().forEach(key => {
        allHeaders[key] = clonedRequest.headers.get(key) || '';
      });
      console.log('📋 AUTH INTERCEPTOR - Todos os headers:', allHeaders);
      
      const curlCommand = this.buildCurlCommand(clonedRequest);
      console.log('📋 AUTH INTERCEPTOR - CURL da requisição GET /users:');
      console.log(curlCommand);

      logger.log('✅ Token adicionado ao header Authorization');
      logger.log('Header Authorization:', `Bearer ${token.substring(0, 20)}...`);

      if (!clonedRequest.headers.get('Authorization')) {
        console.error('❌ AUTH INTERCEPTOR - ERRO: Header Authorization não foi adicionado!');
        console.error('❌ AUTH INTERCEPTOR - Token usado:', token.substring(0, 50) + '...');
      }

      return next.handle(clonedRequest).pipe(
        tap(
          (event) => {
            console.log('✅ AUTH INTERCEPTOR - Requisição bem-sucedida:', url);
            logger.log('✅ Requisição autenticada bem-sucedida:', url);
          },
          (error) => {
            console.error('❌ AUTH INTERCEPTOR - Erro na requisição:', {
              url,
              status: error.status,
              statusText: error.statusText,
              tokenUsed: token.substring(0, 30) + '...'
            });
            if (error.status === 401) {
              logger.warn('⚠️ 401 Unauthorized - Token pode estar inválido ou expirado');
              logger.warn('Token usado:', token.substring(0, 20) + '...');
              
              console.error('🔍 AUTH INTERCEPTOR - Debug 401:', {
                tokenLength: token.length,
                tokenStart: token.substring(0, 50),
                hasBearer: clonedRequest.headers.get('Authorization')?.startsWith('Bearer')
              });
            }
          }
        )
      );
    }

    console.error('❌ AUTH INTERCEPTOR - ERRO CRÍTICO: NENHUM TOKEN ENCONTRADO!');
    console.error('❌ AUTH INTERCEPTOR - A requisição será enviada SEM autenticação!');
    console.error('❌ AUTH INTERCEPTOR - URL da requisição:', url);
    console.error('❌ AUTH INTERCEPTOR - Isso causará erro 401 no backend!');
    
    logger.warn('⚠️ Nenhum token encontrado para a requisição:', url);
    logger.warn('A requisição será enviada sem autenticação');

    return next.handle(request);
  }

  private buildCurlCommand(request: HttpRequest<unknown>): string {
    let curl = `curl -X ${request.method} "${request.url}"`;
    
    request.headers.keys().forEach(key => {
      const value = request.headers.get(key);
      if (value) {
        if (key.toLowerCase() === 'authorization') {
          const token = value.replace('Bearer ', '');
          curl += ` \\\n  -H "${key}: Bearer ${token}"`;
        } else {
          curl += ` \\\n  -H "${key}: ${value}"`;
        }
      }
    });
    
    if (request.body) {
      curl += ` \\\n  -d '${JSON.stringify(request.body)}'`;
    }
    
    return curl;
  }
}
